import { useCallback, useSyncExternalStore } from 'react';

import { getUserStorageOwner, userStorage } from '../utils/userStorage';
import { clearSelectedApplicationIdStore } from './useSelectedApplicationId';

const STORAGE_KEY = 'selectedGroupId';

const createStore = () => {
  let listeners: Array<() => void> = [];
  let currentValue: string | null = null;
  let currentOwner: string | null | undefined;

  const getSnapshot = () => {
    const owner = getUserStorageOwner();
    if (owner !== currentOwner || currentValue === null) {
      currentOwner = owner;
      const stored = userStorage.getItem(STORAGE_KEY);
      currentValue = stored ? JSON.parse(stored) : '';
    }
    return currentValue;
  };

  const subscribe = (listener: () => void) => {
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  };

  const setValue = (value: string, clearApplicationId: boolean = true) => {
    currentOwner = getUserStorageOwner();
    currentValue = value;
    if (clearApplicationId) {
      clearSelectedApplicationIdStore();
    }
    userStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    listeners.forEach((listener) => listener());
  };

  const clearValue = () => {
    currentOwner = getUserStorageOwner();
    currentValue = '';
    userStorage.removeItem(STORAGE_KEY);
    listeners.forEach((listener) => listener());
  };

  return { getSnapshot, subscribe, setValue, clearValue };
};

const store = createStore();

export const useSelectedApplicationGroupId = () => {
  const selectedGroupId = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );

  const setSelectedGroupId = useCallback((groupId: string) => {
    store.setValue(groupId);
  }, []);

  const clearSelectedGroupId = useCallback(() => {
    store.clearValue();
  }, []);

  return {
    selectedGroupId,
    setSelectedGroupId,
    clearSelectedGroupId,
  };
};

export default useSelectedApplicationGroupId;
